'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { imageDimensions } from '@eliadmin/core/src/utils/image-dimensions';
import { BANNER_IMAGE_CONFIG } from '@eliadmin/core/src/constants/banner-image-config';


import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

export type BannerGridProps = {
  className?: {
    main?: string;
    bannerLink?: string;
    bannerImage?: string;
  };
};

export function BannerGrid(props: BannerGridProps) {
  const locale = useLocaleStore(s => s.currentLocale);

  const { data: banners } = useSuspenseQuery({
    queryKey: [CacheKey.BANNERS, 'banner', locale],
    queryFn: () =>
      fetchBanners({
        locale,
        query: { where: { type: { equals: 'banner' } }, results: 15 },
      }),
  });

  const desktopBanners = banners.data?.filter(i => i.types?.includes('desktop') && i.image);
  const mobileBanners = banners.data?.filter(i => i.types?.includes('mobile') && i.image);

  if (!desktopBanners.length && !mobileBanners.length) return null;

  return (
    <>
      <div
        className={cn(
          'hidden w-full grid-cols-3 gap-3 pt-4 md:grid',
          'ea-banner-grid__desktop-wrapper',
        )}
      >
        {desktopBanners.map((banner, index) => {
          if (!banner.image) return null;
          const dimensions = imageDimensions(banner.image.metadata) ??
            imageDimensions(BANNER_IMAGE_CONFIG.dimensions[banner.image.url]);

          return (
            <Link href={banner?.url} key={index} className="col-span-1 w-full">
              <Image
                src={banner?.image?.url}
                alt={resolveImageAlt(banner?.image, banner?.name)}
                width={dimensions?.width ?? 0}
                height={dimensions?.height ?? 0}
                className="col-span-1 h-auto w-full rounded-sm object-cover"
                sizes="(min-width: 1280px) 408px, calc((100vw - 56px) / 3)"
              />
            </Link>
          );
        })}
      </div>
      <div
        className={cn(
          'grid grid-cols-2 gap-2 pt-6 md:hidden',
          'ea-banner-grid ea-banner-grid__mobile',
          props.className?.main,
        )}
      >
        {mobileBanners.map((banner, index) => {
          if (!banner.image) return null;
          const dimensions = imageDimensions(banner.image.metadata) ??
            imageDimensions(BANNER_IMAGE_CONFIG.dimensions[banner.image.url]);
          return (
            <div
              className={cn(
                'col-span-1 h-full w-full overflow-hidden rounded-sm',
                'ea-banner-grid__container ea-banner-grid__container__mobile',
              )}
              key={index}
            >
              <Link
                href={banner.url ?? '#'}
                className={cn(
                  'ea-banner-grid__link ea-banner-grid__link__mobile overflow-hidden rounded-md',
                  props.className?.bannerLink,
                )}
                prefetch={false}
              >
                <Image
                  alt={resolveImageAlt(banner?.image, banner?.name, 'image')}
                  src={banner.image.url}
                  width={dimensions?.width ?? 0}
                  height={dimensions?.height ?? 0}
                  sizes="calc((100vw - 40px) / 2)"
                  quality={75}
                  className={cn(
                    'w-full object-contain',
                    'ea-homepage-banner-grid__link-image ea-homepage-banner-grid__link-image__mobile',
                    props.className?.bannerImage,
                  )}
                />
              </Link>
            </div>
          );
        })}
      </div>
    </>
  );
}
